import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  SearchDocumentsInput,
  SearchDocumentsResponse,
  SearchDocumentResult,
} from '@erp/rpc';

/**
 * Hook for searching documents (POs, SOs, and Shipments) for invoice matching
 *
 * @param input - Search parameters
 * @param enabled - Whether to enable the query
 * @returns Query result with search results
 */
export function useDocumentSearch(input: SearchDocumentsInput, enabled = true) {
  const query = useQuery(
    orpc.accountsPayable.documentSearch.search.queryOptions({ input, enabled }),
  );

  const results = query.data?.results ?? [];
  const pagination = query.data?.pagination ?? {
    total: 0,
    limit: 25,
    offset: 0,
    hasMore: false,
  };

  return {
    ...query,
    results,
    pagination,
  } satisfies typeof query & {
    data?: SearchDocumentsResponse;
    results: SearchDocumentResult[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

/**
 * Hook for searching documents with debounced input
 * Useful for autocomplete scenarios
 */
export function useDocumentSearchDebounced(
  input: SearchDocumentsInput,
  enabled = true,
) {
  return useDocumentSearch(input, enabled);
}
